import {
    Cookie,
    Loader2,
    MonitorSmartphone,
    ShieldAlert,
    Sparkles,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useChainId, useSignTypedData } from 'wagmi';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
// the store module only, not the feature barrel: the barrel pulls the
// onboarding flow back into this module's own dependencies.
import { useOnboardingLauncher } from '@/features/onboarding/store';
import { useWallet } from '@/features/wallet/hooks/useWallet';
import { hasInjectedWalletProvider } from '@/features/wallet/injectedWallet';
import { ensureTypedDataChain } from '@/features/wallet/services/walletChain';
import { useWalletStore } from '@/features/wallet/store';
// the cookie-probe module directly, not the api barrel: the dialog only needs
// this one call and the barrel drags the whole trading-session client in.
import { probeTradingSessionCookieSupport } from '@/lib/api/tradingSession/cookieProbe';
import {
    describeErrorText,
    isUserRejection,
    WalletSignatureError,
} from '@/lib/errors';

import { broadcastTradingSession } from '../broadcast';
import { DEFAULT_TRADING_WINDOW_MINUTES } from '../defaultWindowPolicy';
import { resumeDurableJob } from '../durableJobResume';
import {
    completeTradingSessionCeremony,
    isPasskeyOwnerHintStaleError,
    isTradingSessionCookieUnavailableError,
} from '../sessionCeremony';
import { useTradingSessionStore } from '../store';
import {
    isTradingSessionWalletMissingError,
    resolveSessionAnchor,
} from '../walletResolution';

type MissingAccountCode = 'authorizationWalletMissing' | 'tradingWalletMissing';

const TradingSessionDialog: React.FC = () => {
    const open = useTradingSessionStore((state) => state.reauthenticationOpen);
    const status = useTradingSessionStore((state) => state.status);
    const preferredMethod = useTradingSessionStore(
        (state) => state.preferredMethod,
    );
    const walletRequirement = useTradingSessionStore(
        (state) => state.walletRequirement,
    );
    const close = useTradingSessionStore(
        (state) => state.closeReauthentication,
    );
    const pendingJobId = useTradingSessionStore((state) => state.pendingJobId);
    const clearPendingJob = useTradingSessionStore(
        (state) => state.clearPendingJob,
    );
    const applyStatus = useTradingSessionStore((state) => state.applyStatus);
    const saveAppSession = useWalletStore((state) => state.saveAppSession);
    const { address, isConnected, connect } = useWallet();
    // tie-break hint only: the ceremony picks the anchor from which managers
    // actually hold a wallet for this owner, preferring the connected chain.
    const connectedChainId = useChainId();
    const { signTypedDataAsync } = useSignTypedData();
    const [method, setMethod] = useState<'passkey' | 'eip712'>('eip712');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // the browser drops our cross-site trading cookie, so a signature would be
    // spent for nothing: the guidance panel replaces the error and the submit
    // stays disabled until a re-check clears it.
    const [cookieBlocked, setCookieBlocked] = useState(false);
    const [isCheckingCookie, setIsCheckingCookie] = useState(false);
    const [missingAccount, setMissingAccount] =
        useState<MissingAccountCode | null>(null);
    // the browser has no wallet to connect (no extension, not a wallet app's
    // browser) and nothing is connected: the wallet window cannot run here.
    const [walletUnavailable, setWalletUnavailable] = useState(false);
    const [probeSettled, setProbeSettled] = useState(false);
    // the wallet path checks the account exists before asking for a signature
    const [identitySettled, setIdentitySettled] = useState(false);
    // true only once this open's resets below have been applied. Without it
    // the auto-run effect would fire in the same commit as the reset effect
    // and see the previous open's method and probe result (a passkey sign-in
    // followed by a wallet-only operation ran the passkey again).
    const [armed, setArmed] = useState(false);
    const launchOnboarding = useOnboardingLauncher((state) => state.launch);
    // once a seeded owner turned out stale (passkeyNotFound), stop seeding so
    // the retry falls back to discovery instead of failing the same way again.
    const skipKnownOwnerRef = useRef(false);
    // one probe per dialog-open, and only the newest one may write state
    const probeStartedRef = useRef(false);
    const probeTokenRef = useRef(0);
    // the dialog asks for its signature by itself, once per open: a failure
    // leaves a "Try again" button rather than a second prompt.
    const autoSignAttemptedRef = useRef(false);
    const [connectAttempted, setConnectAttempted] = useState(false);
    const handleSubmitRef = useRef<() => Promise<void>>(async () => {});
    const walletMethod = method === 'eip712';

    useEffect(() => {
        if (!open) {
            setArmed(false);
            return;
        }
        setMethod(preferredMethod);
        setError(null);
        setCookieBlocked(false);
        setProbeSettled(false);
        setIdentitySettled(false);
        setMissingAccount(null);
        setWalletUnavailable(false);
        skipKnownOwnerRef.current = false;
        autoSignAttemptedRef.current = false;
        setConnectAttempted(false);
        // batched with the resets above: the next render sees all of them
        setArmed(true);
    }, [open, preferredMethod]);

    // Both methods this dialog offers (eip712, passkey) mint the HttpOnly
    // trading cookie, and WebKit drops it because the app and the API sit on
    // different registrable domains. Ask before the signature, once per open,
    // so the failure never costs a wallet prompt. In-flight or `unknown`
    // (older backend, network trouble) changes nothing.
    useEffect(() => {
        if (!open) {
            probeStartedRef.current = false;
            return;
        }
        if (probeStartedRef.current) return;
        probeStartedRef.current = true;
        const token = ++probeTokenRef.current;
        void probeTradingSessionCookieSupport()
            .then((support) => {
                if (probeTokenRef.current !== token) return;
                setCookieBlocked(support === 'blocked');
                setProbeSettled(true);
            })
            .catch(() => {
                /* probeTradingSessionCookieSupport never rejects */
            });
    }, [open]);

    const recheckCookieSupport = async () => {
        const token = ++probeTokenRef.current;
        setIsCheckingCookie(true);
        try {
            const support = await probeTradingSessionCookieSupport();
            if (probeTokenRef.current !== token) return;
            setCookieBlocked(support === 'blocked');
            if (support !== 'blocked') setError(null);
        } finally {
            setIsCheckingCookie(false);
        }
    };

    // The ceremony resolves the identity before any HTTP call or signature, so
    // reading it up front costs nothing extra and turns "click, sign, dead end"
    // into a setup offer the moment the dialog opens.
    useEffect(() => {
        if (!open || method !== 'eip712' || !address) return;
        let cancelled = false;
        setIdentitySettled(false);
        void resolveSessionAnchor(address, connectedChainId)
            .then(() => {
                if (!cancelled) setMissingAccount(null);
            })
            .catch((caught) => {
                if (cancelled) return;
                if (
                    isTradingSessionWalletMissingError(caught) &&
                    caught.code === 'authorizationWalletMissing'
                ) {
                    setMissingAccount(caught.code);
                }
            })
            .finally(() => {
                if (!cancelled) setIdentitySettled(true);
            });
        return () => {
            cancelled = true;
        };
    }, [address, connectedChainId, method, open]);

    const startAccountSetup = () => {
        close();
        launchOnboarding(
            missingAccount === 'tradingWalletMissing' ? 'voucher' : 'welcome',
            { resumeReauthOnFinish: true },
        );
    };

    const handleSubmit = async () => {
        setError(null);
        setMissingAccount(null);
        if (method === 'eip712' && !address) {
            try {
                await connect();
            } catch (caught) {
                setError(
                    describeErrorText(caught, {
                        operation: 'Wallet connection',
                    }),
                );
            }
            return;
        }
        setIsSubmitting(true);
        try {
            const completed =
                method === 'eip712'
                    ? await completeTradingSessionCeremony({
                          method,
                          durationMinutes: DEFAULT_TRADING_WINDOW_MINUTES,
                          preferredAnchorChainId: connectedChainId,
                          wallet: address!,
                          signTypedData: async (typedData) => {
                              // the domain names the anchor; MetaMask refuses typed
                              // data signed from any other active chain
                              await ensureTypedDataChain(typedData);
                              try {
                                  return await signTypedDataAsync(
                                      typedData as Parameters<
                                          typeof signTypedDataAsync
                                      >[0],
                                  );
                              } catch (caught) {
                                  // a cancel stays a cancel; anything else the wallet
                                  // says is the wallet's failure, presented as such
                                  // (never as "our side") with its text in the console
                                  if (isUserRejection(caught)) throw caught;
                                  throw new WalletSignatureError(caught);
                              }
                          },
                      })
                    : await completeTradingSessionCeremony({
                          method,
                          durationMinutes: DEFAULT_TRADING_WINDOW_MINUTES,
                          preferredAnchorChainId: connectedChainId,
                          // reauthenticating a passkey-authored session: its owner
                          // is already known, so skip the discovery prompt. Only
                          // seed from PASSKEY sessions — an eip712 owner may hold
                          // no passkey at all and would dead-end the sign-in.
                          ...(!skipKnownOwnerRef.current &&
                          status?.state === 'active' &&
                          status.method === 'passkey' &&
                          status.owner
                              ? { knownOwner: status.owner }
                              : {}),
                      });
            applyStatus(completed.status);
            saveAppSession(
                completed.appToken.token,
                completed.appToken.expiresAtMs,
                completed.appToken.owner && completed.appToken.sessionId
                    ? {
                          owner: completed.appToken.owner,
                          sessionId: completed.appToken.sessionId,
                      }
                    : undefined,
            );
            broadcastTradingSession({ type: 'changed' });
            if (pendingJobId) {
                await resumeDurableJob(pendingJobId, completed.appToken.token);
                clearPendingJob();
            }
            close();
        } catch (caught) {
            if (isPasskeyOwnerHintStaleError(caught)) {
                skipKnownOwnerRef.current = true;
            }
            if (isTradingSessionWalletMissingError(caught)) {
                setMissingAccount(caught.code);
                return;
            }
            if (isTradingSessionCookieUnavailableError(caught)) {
                // the guidance panel carries the whole explanation, and the
                // submit stays disabled so a retry cannot burn one more
                // signature against the same browser setting.
                setCookieBlocked(true);
                return;
            }
            setError(
                describeErrorText(caught, { operation: 'Trading session' }),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    handleSubmitRef.current = handleSubmit;

    // The dialog runs on its own: connect if the wallet path has nothing
    // connected, then request the signature or passkey. It waits for the
    // cookie probe (and, on the wallet path, the account check) so a blocked
    // browser or a missing account never costs a prompt, and it never fires
    // twice per open.
    useEffect(() => {
        if (!open || !armed || !probeSettled || cookieBlocked) return;
        if (missingAccount || isSubmitting || error) return;
        if (walletMethod && !address) {
            if (connectAttempted) return;
            setConnectAttempted(true);
            if (!isConnected && !hasInjectedWalletProvider()) {
                setWalletUnavailable(true);
                return;
            }
            void connect().catch((caught) =>
                setError(
                    describeErrorText(caught, {
                        operation: 'Wallet connection',
                    }),
                ),
            );
            return;
        }
        if (walletMethod && !identitySettled) return;
        if (autoSignAttemptedRef.current) return;
        autoSignAttemptedRef.current = true;
        setWalletUnavailable(false);
        void handleSubmitRef.current();
    }, [
        address,
        armed,
        connect,
        connectAttempted,
        cookieBlocked,
        error,
        identitySettled,
        isConnected,
        isSubmitting,
        missingAccount,
        open,
        probeSettled,
        walletMethod,
    ]);

    const retryConnect = () => {
        void connect().catch((caught) =>
            setError(
                describeErrorText(caught, { operation: 'Wallet connection' }),
            ),
        );
    };

    const retryAutoSign = () => {
        setError(null);
        autoSignAttemptedRef.current = true;
        void handleSubmit();
    };

    // the operation's chain is its own business now; the copy only has to say
    // WHY a connected wallet is needed, not where the transaction lands
    const requirementText = walletRequirement
        ? walletRequirement.reason === 'anchorTransaction'
            ? `${walletRequirement.operation} requires a connected wallet because it sends an on-chain transaction from your wallet.`
            : `${walletRequirement.operation} requires a connected wallet for security reasons.`
        : null;
    const waiting =
        !walletUnavailable && !missingAccount && !cookieBlocked && !error;

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => !next && !isSubmitting && close()}
        >
            {/* The AppKit connect modal opens OVER this dialog, and Radix
                treats every interaction in it as an outside interaction; the
                default dismiss would kill the ceremony mid-connect, which is
                exactly what happened to wallet-less sign-ins. Dismissal is the
                Cancel button. */}
            <DialogContent
                onPointerDownOutside={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
                onEscapeKeyDown={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>
                        {walletRequirement
                            ? `${walletRequirement.operation} requires a connected wallet`
                            : method === 'passkey'
                              ? 'Confirm with your passkey'
                              : 'Confirm in your wallet'}
                    </DialogTitle>
                    <DialogDescription>
                        {method === 'passkey'
                            ? `Use your passkey (face, fingerprint or PIN) to start trading for the next ${DEFAULT_TRADING_WINDOW_MINUTES} minutes. Nothing is sent on chain, no gas is spent and no funds move.`
                            : `${requirementText ? `${requirementText} ` : ''}Approve one signature in your wallet to continue for the next ${DEFAULT_TRADING_WINDOW_MINUTES} minutes. It is a signature only (no transaction, no gas, no funds move).`}
                    </DialogDescription>
                </DialogHeader>
                {missingAccount && (
                    <div className="flex gap-2 rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm">
                        <Sparkles className="size-4 shrink-0 text-primary" />
                        <div>
                            <p className="font-semibold text-foreground">
                                This wallet doesn’t have a Snappy account yet.
                            </p>
                            <p className="mt-1 text-muted-foreground">
                                Set one up once (it takes a couple of
                                signatures) and this trading window opens right
                                after.
                            </p>
                            <Button
                                className="mt-3 w-full"
                                onClick={startAccountSetup}
                            >
                                Set up your account
                            </Button>
                        </div>
                    </div>
                )}
                {walletUnavailable && (
                    <div
                        role="alert"
                        className="flex gap-2 rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm"
                    >
                        <MonitorSmartphone className="size-4 shrink-0 text-primary" />
                        <div>
                            <p className="font-semibold text-foreground">
                                Continue on a browser with a connected wallet.
                            </p>
                            <p className="mt-1 text-muted-foreground">
                                {requirementText ??
                                    'Signing in this way requires a connected wallet.'}{' '}
                                This browser has no wallet to connect. Open
                                app.snappy.trade in a browser with your wallet
                                extension, or in your wallet app's built-in
                                browser, and try again there.
                            </p>
                            <Button
                                variant="outline"
                                className="mt-3 w-full"
                                onClick={() => {
                                    setWalletUnavailable(false);
                                    void connect().catch((caught) =>
                                        setError(
                                            describeErrorText(caught, {
                                                operation: 'Wallet connection',
                                            }),
                                        ),
                                    );
                                }}
                            >
                                Connect a wallet app instead
                            </Button>
                        </div>
                    </div>
                )}
                {waiting && (
                    <div
                        role="status"
                        className="flex items-center gap-3 rounded-xl border border-border bg-secondary/30 p-3 text-sm"
                    >
                        <Loader2 className="size-5 shrink-0 animate-spin text-primary" />
                        <span className="font-medium text-foreground">
                            {method === 'passkey'
                                ? 'Confirm with your passkey'
                                : address
                                  ? 'Confirm in your wallet'
                                  : 'Connect your wallet to continue'}
                        </span>
                    </div>
                )}
                {waiting && walletMethod && !address && connectAttempted && (
                    <Button
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={retryConnect}
                    >
                        Connect wallet
                    </Button>
                )}
                {cookieBlocked && (
                    <div
                        role="alert"
                        className="flex gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm"
                    >
                        <Cookie className="size-4 shrink-0 text-destructive" />
                        <div>
                            <p className="font-semibold text-foreground">
                                Your browser is blocking the secure trading
                                cookie.
                            </p>
                            <p className="mt-1 text-muted-foreground">
                                Safari and in-app browsers block cookies between
                                our app and our secure API. This is a device
                                setting, not a problem with your account.
                            </p>
                            <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
                                <li>
                                    Open app.snappy.trade in your phone’s
                                    regular browser instead of an in-app
                                    browser.
                                </li>
                                <li>
                                    On iPhone Safari, turn off Settings, then
                                    Safari, then Prevent Cross-Site Tracking,
                                    and reload this page.
                                </li>
                                <li>Or use Snappy from a desktop browser.</li>
                            </ul>
                            <Button
                                variant="outline"
                                className="mt-3 w-full"
                                disabled={isCheckingCookie}
                                onClick={() => void recheckCookieSupport()}
                            >
                                {isCheckingCookie && (
                                    <Loader2 className="animate-spin" />
                                )}
                                Check again
                            </Button>
                        </div>
                    </div>
                )}
                {error && !cookieBlocked && (
                    <div
                        role="alert"
                        className="flex gap-2 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                    >
                        <ShieldAlert className="size-4 shrink-0" />
                        <div>
                            <p>{error}</p>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button
                        variant="ghost"
                        disabled={isSubmitting}
                        onClick={close}
                    >
                        Cancel
                    </Button>
                    {error && !missingAccount && (
                        <Button
                            disabled={isSubmitting || cookieBlocked}
                            onClick={retryAutoSign}
                        >
                            Try again
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TradingSessionDialog;
